export type CaptureStatus = 'idle' | 'requesting' | 'selected' | 'connecting' | 'active' | 'interrupted' | 'error'
export type CaptureMediaState = 'idle' | 'playing' | 'waiting' | 'stalled' | 'paused' | 'muted' | 'unmuted'
export type CaptureFailureKind = 'permission-denied' | 'source-unavailable' | 'timeout' | 'unsupported' | 'unknown'
export type CaptureSelectionPreference = 'window' | 'monitor'
export type CaptureSurface = CaptureSelectionPreference | 'browser'
export type CaptureFailurePhase = 'selection' | 'track' | 'play' | 'dimensions'

export interface CaptureDiagnostic {
  readonly recordedAt: string
  readonly phase: CaptureFailurePhase
  readonly name: string
  readonly message: string
  readonly requestedSurface: CaptureSelectionPreference
  readonly displaySurface?: CaptureSurface
  readonly track?: { readonly readyState: MediaStreamTrackState; readonly muted: boolean }
  readonly browser: string
}

export class CaptureSelectionReplacedError extends Error {
  constructor() {
    super('Capture selection was replaced')
    this.name = 'AbortError'
  }
}

export function createCaptureDiagnostic(error: unknown, phase: CaptureFailurePhase, requestedSurface: CaptureSelectionPreference, track?: MediaStreamTrack): CaptureDiagnostic {
  let displaySurface: CaptureSurface | undefined
  let browser = 'unknown'
  // Diagnostics must never prevent failed streams from being released.
  try { displaySurface = captureSurface(track?.getSettings().displaySurface) } catch { /* source may already be unavailable */ }
  try { browser = navigator.userAgent } catch { /* browser details are optional */ }
  return {
    recordedAt: new Date().toISOString(),
    phase,
    name: typeof error === 'object' && error !== null && 'name' in error ? String(error.name) : 'Error',
    message: captureErrorMessage(error),
    requestedSurface,
    displaySurface,
    track: track ? { readyState: track.readyState, muted: track.muted } : undefined,
    browser,
  }
}

function captureSurface(value: string | undefined): CaptureSurface | undefined {
  return value === 'window' || value === 'monitor' || value === 'browser' ? value : undefined
}

function captureErrorMessage(error: unknown): string {
  return typeof error === 'object' && error !== null && 'message' in error ? String(error.message) : String(error)
}

export interface CaptureInfo {
  readonly width: number
  readonly height: number
  readonly label: string
  readonly displaySurface?: CaptureSurface
}

interface ExtendedDisplayMediaStreamOptions extends DisplayMediaStreamOptions {
  readonly monitorTypeSurfaces?: 'include' | 'exclude'
  readonly selfBrowserSurface?: 'include' | 'exclude'
  readonly surfaceSwitching?: 'include' | 'exclude'
}

const DISPLAY_CAPTURE_OPTIONS: ExtendedDisplayMediaStreamOptions = {
  video: { displaySurface: 'window', frameRate: { ideal: 10 } },
  audio: false,
  monitorTypeSurfaces: 'include',
  selfBrowserSurface: 'exclude',
  surfaceSwitching: 'exclude',
}

export function classifyCaptureFailure(error: unknown): CaptureFailureKind {
  const name = typeof error === 'object' && error !== null && 'name' in error
    ? String(error.name)
    : ''
  const message = captureErrorMessage(error)

  if (name === 'NotAllowedError' || /permission denied|not allowed by the user|permission dismissed/i.test(message)) {
    return 'permission-denied'
  }
  if (name === 'NotSupportedError' || name === 'SecurityError' || /display capture is not supported/i.test(message)) {
    return 'unsupported'
  }
  if (name === 'NotReadableError'
    || name === 'NotFoundError'
    || name === 'AbortError'
    || name === 'OverconstrainedError'
    || /could not start (?:video )?source|starting (?:video|screen).*failed|failed to (?:start|allocate).*video|video source.*(?:unavailable|busy|in use)/i.test(message)) {
    return 'source-unavailable'
  }
  if (/等待捕获画面超时|capture frame timed out|timed out waiting for capture/i.test(message)) {
    return 'timeout'
  }
  return 'unknown'
}

export class CaptureService extends EventTarget {
  readonly video: HTMLVideoElement
  private stream: MediaStream | null = null
  private selectionRevision = 0
  private cancelConnection: (() => void) | null = null
  private requestedSurface: CaptureSelectionPreference = 'window'
  status: CaptureStatus = 'idle'
  mediaState: CaptureMediaState = 'idle'
  lastError: string | null = null
  lastDiagnostic: CaptureDiagnostic | null = null

  get selectionId(): number {
    return this.selectionRevision
  }

  constructor() {
    super()
    this.video = document.createElement('video')
    this.video.muted = true
    this.video.playsInline = true
    this.video.autoplay = true
    this.video.addEventListener('playing', () => {
      if (this.stream) this.setMediaState('playing')
    })
    this.video.addEventListener('waiting', () => {
      if (this.stream) this.setMediaState('waiting')
    })
    this.video.addEventListener('stalled', () => {
      if (this.stream) this.setMediaState('stalled')
    })
    this.video.addEventListener('pause', () => {
      if (this.stream) this.setMediaState('paused')
    })
  }

  get supported(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  }

  async selectWindow(preference: CaptureSelectionPreference = 'window'): Promise<CaptureInfo> {
    this.stop()
    const selectionRevision = ++this.selectionRevision
    this.lastError = null
    this.lastDiagnostic = null
    this.requestedSurface = preference
    this.setStatus('requesting')
    let requestedStream: MediaStream | null = null
    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new DOMException('Display capture is not supported by this browser', 'NotSupportedError')
      }
      const stream = await navigator.mediaDevices.getDisplayMedia({
        ...DISPLAY_CAPTURE_OPTIONS,
        video: { displaySurface: preference, frameRate: { ideal: 10 } },
      })
      requestedStream = stream
      if (selectionRevision !== this.selectionRevision) {
        throw new CaptureSelectionReplacedError()
      }
      const track = stream.getVideoTracks()[0]
      if (!track) throw new Error('未获得视频轨道')
      this.stream = stream
      track.addEventListener('ended', () => {
        if (this.stream !== stream) return
        this.setStatus('interrupted')
        this.dispatchEvent(new Event('ended'))
      }, { once: true })
      track.addEventListener('mute', () => {
        if (this.stream === stream) this.setMediaState('muted')
      })
      track.addEventListener('unmute', () => {
        if (this.stream === stream) this.setMediaState('unmuted')
      })
      const settings = track.getSettings()
      this.setStatus('selected')
      return {
        width: settings.width || 0,
        height: settings.height || 0,
        label: track.label || 'MapleStory',
        displaySurface: captureSurface(settings.displaySurface),
      }
    } catch (error) {
      if (selectionRevision !== this.selectionRevision) {
        this.discardStream(requestedStream)
        throw new CaptureSelectionReplacedError()
      }
      this.lastDiagnostic = createCaptureDiagnostic(error, 'selection', preference, requestedStream?.getVideoTracks()[0])
      this.discardStream(requestedStream)
      this.setMediaState('idle')
      this.lastError = `${this.lastDiagnostic.name}: ${this.lastDiagnostic.message}`
      this.setStatus('error')
      throw error
    }
  }

  async connectSelectedWindow(): Promise<CaptureInfo> {
    const selectionRevision = this.selectionRevision
    const stream = this.stream
    const track = stream?.getVideoTracks()[0]
    let phase: CaptureFailurePhase = 'track'
    try {
      if (!stream || !track || track.readyState === 'ended') {
        throw new DOMException('Selected video source is no longer available', 'NotReadableError')
      }
      if (this.status === 'active') return this.captureInfo(track)
      this.lastError = null
      this.lastDiagnostic = null
      this.setStatus('connecting')
      this.video.srcObject = stream
      phase = 'play'
      await this.waitForConnection(track, () => { phase = 'dimensions' })
      if (selectionRevision !== this.selectionRevision) throw new CaptureSelectionReplacedError()
      if (this.stream !== stream || String(track.readyState) === 'ended') {
        throw new DOMException('Selected video source ended while connecting', 'NotReadableError')
      }
      this.setStatus('active')
      this.setMediaState(track.muted ? 'muted' : 'playing')
      return this.captureInfo(track)
    } catch (error) {
      if (selectionRevision !== this.selectionRevision) {
        this.discardStream(stream)
        throw new CaptureSelectionReplacedError()
      }
      this.lastDiagnostic = createCaptureDiagnostic(error, phase, this.requestedSurface, track)
      this.discardStream(stream)
      this.setMediaState('idle')
      this.lastError = `${this.lastDiagnostic.name}: ${this.lastDiagnostic.message}`
      this.setStatus('error')
      throw error
    }
  }

  get mediaStream(): MediaStream | null {
    return this.stream
  }

  async snapshot(): Promise<ImageBitmap> {
    if (this.status !== 'active' || this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) throw new Error('捕获画面尚未就绪')
    return createImageBitmap(this.video)
  }

  stop(): void {
    this.selectionRevision += 1
    this.cancelConnection?.()
    const stream = this.stream
    this.stream = null
    for (const track of stream?.getTracks() ?? []) track.stop()
    this.video.srcObject = null
    this.setMediaState('idle')
    if (this.status !== 'idle') this.setStatus('idle')
  }

  private setStatus(status: CaptureStatus): void {
    this.status = status
    this.dispatchEvent(new CustomEvent('status', { detail: status }))
  }

  private setMediaState(state: CaptureMediaState): void {
    if (this.mediaState === state) return
    this.mediaState = state
    this.dispatchEvent(new CustomEvent('media-state', { detail: state }))
  }

  private discardStream(stream: MediaStream | null): void {
    if (!stream) return
    if (this.stream === stream) this.stream = null
    if (this.video.srcObject === stream) this.video.srcObject = null
    for (const track of stream.getTracks()) track.stop()
  }

  private captureInfo(track: MediaStreamTrack): CaptureInfo {
    const settings = track.getSettings()
    return {
      width: this.video.videoWidth || settings.width || 0,
      height: this.video.videoHeight || settings.height || 0,
      label: track.label || 'MapleStory',
      displaySurface: captureSurface(settings.displaySurface),
    }
  }

  private waitForConnection(track: MediaStreamTrack, onPlaying: () => void): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false
      let playing = false
      const finish = (error?: unknown) => {
        if (settled) return
        settled = true
        cleanup()
        if (error) reject(error)
        else resolve()
      }
      const checkDimensions = () => {
        if (playing && this.video.videoWidth > 0 && this.video.videoHeight > 0) finish()
      }
      const ended = () => finish(new DOMException('Selected video source ended while connecting', 'NotReadableError'))
      const cancel = () => finish(new CaptureSelectionReplacedError())
      const timer = window.setTimeout(() => finish(new Error('Capture frame timed out')), 8_000)
      const cleanup = () => {
        window.clearTimeout(timer)
        this.video.removeEventListener('loadedmetadata', checkDimensions)
        this.video.removeEventListener('resize', checkDimensions)
        track.removeEventListener('ended', ended)
        if (this.cancelConnection === cancel) this.cancelConnection = null
      }
      this.cancelConnection = cancel
      this.video.addEventListener('loadedmetadata', checkDimensions)
      this.video.addEventListener('resize', checkDimensions)
      track.addEventListener('ended', ended, { once: true })
      try {
        void this.video.play().then(() => {
          if (settled) return
          playing = true
          onPlaying()
          checkDimensions()
        }, finish)
      } catch (error) {
        finish(error)
      }
    })
  }
}

export const captureService = new CaptureService()
